var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useCallback, useRef, } from "react";
import applyRef from "../applyRef";
import useFocusOnMount from "./useFocusOnMount";
import usePreviousFocus from "./usePreviousFocus";
import useTabFocusWrap from "./useTabFocusWrap";
/**
 * The `FocusContainer` is a wrapper for a few of the accessibility hooks to
 * maintain focus within an element.
 */
var FocusContainer = forwardRef(function FocusContainer(_a, forwardedRef) {
    var children = _a.children, onKeyDown = _a.onKeyDown, _b = _a.component, Component = _b === void 0 ? "div" : _b, _c = _a.defaultFocus, defaultFocus = _c === void 0 ? "first" : _c, _d = _a.disableFocusCache, disableFocusCache = _d === void 0 ? false : _d, _e = _a.disableFocusOnMount, disableFocusOnMount = _e === void 0 ? false : _e, _f = _a.disableFocusOnMountScroll, disableFocusOnMountScroll = _f === void 0 ? false : _f, _g = _a.disableFocusOnUnmount, disableFocusOnUnmount = _g === void 0 ? false : _g, _h = _a.disableTabFocusWrap, disableTabFocusWrap = _h === void 0 ? false : _h, _j = _a.unmountFocusFallback, unmountFocusFallback = _j === void 0 ? "" : _j, props = __rest(_a, ["children", "onKeyDown", "component", "defaultFocus", "disableFocusCache", "disableFocusOnMount", "disableFocusOnMountScroll", "disableFocusOnUnmount", "disableTabFocusWrap", "unmountFocusFallback"]);
    var ref = useRef(null);
    var refHandler = useCallback(function (instance) {
        applyRef(instance, forwardedRef);
        ref.current = instance;
    }, [forwardedRef]);
    usePreviousFocus(disableFocusOnUnmount, unmountFocusFallback);
    useFocusOnMount(ref, defaultFocus, disableFocusOnMountScroll, false, disableFocusOnMount);
    var handleKeyDown = useTabFocusWrap({
        disabled: disableTabFocusWrap,
        disableFocusCache: disableFocusCache,
        onKeyDown: onKeyDown,
    });
    return (React.createElement(Component, __assign({}, props, { onKeyDown: handleKeyDown, ref: refHandler }), children));
});
if (process.env.NODE_ENV !== "production") {
    try {
        var PropTypes = require("prop-types");
        FocusContainer.propTypes = {
            disableFocusCache: PropTypes.bool,
            disableFocusOnMount: PropTypes.bool,
            disableFocusOnUnmount: PropTypes.bool,
            disableTabFocusWrap: PropTypes.bool,
            defaultFocus: PropTypes.oneOfType([
                PropTypes.oneOf(["first", "last"]),
                PropTypes.string,
            ]),
            component: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.func,
                PropTypes.object,
            ]),
            unmountFocusFallback: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.func,
                PropTypes.object,
            ]),
            children: PropTypes.node,
            onKeyDown: PropTypes.func,
            disableFocusOnMountScroll: PropTypes.bool,
        };
    }
    catch (e) { }
}
export default FocusContainer;
//# sourceMappingURL=FocusContainer.js.map